import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
	static targets = ['modal', 'container', 'step', 'cta', 'loginEmail', 'loginPassword', 'guestAccessField', 'closeModalButton'];

	static values = {
		/**
		 * The WP admin-ajax.php URL to send AJAX requests to
		 */
		url: String,

		/**
		 * The (optional) post_id of the content item
		 */
		postId: Number,

		/**
		 * The (optional) collection_id of the content item
		 */
		collectionId: Number,
	};

	connect() {
		/**
		 * Check for attribute to know if the modal needs to be opened onload
		 */
		if (this.ctaTarget.dataset.accountModalOpenInstantly !== undefined && this.ctaTarget.dataset.accountModalOpenInstantly == 'true') {
			this.open();
		}
	}

	/**
	 * Open the modal
	 */
	open() {
		this.modalTarget.classList.remove('hidden');
		// document.body.style.filter = "grayscale(100%)";
	}

	/**
	 * Close the modal
	 */
	close(e) {
		e.preventDefault();
		this.modalTarget.classList.add('hidden');
		// document.body.style.filter = "";
	}

	/**
	 * As an alternative to the "close" method, the back method sends the
	 * user back to the previous page on clicking the X of the modal
	 */
	back() {
		history.back();

		/**
		 * If there is no history, nothing will happen: redirect to home
		 */
		setTimeout(() => {
			window.location.href = '/';
		}, 500);
	}

	/**
	 * Go to step in the modal defined bu Stimulus param attribute in the
	 * event triggering element
	 */
	goToStep(e) {
		const step = parseInt(e.params.step);
		this.activateStep(step);
	}

	/**
	 * Activate specified step of modal
	 */
	activateStep(step) {
		for (let i in this.stepTargets) {
			i = parseInt(i);
			/**
			 * Display specified step, hide all others
			 */
			if (i + 1 === step) {
				this.stepTargets[i].classList.remove('hidden');
			} else {
				this.stepTargets[i].classList.add('hidden');
			}
		}

		/**
		 * On mobile, the div with the overflow-scroll needs to be scrolled to the top
		 * or user will be scrolled half way newly loaded steps
		 */
		this.containerTarget.scrollTop = 0;

		this.dispatch('modalStep', { detail: { step: step } });
	}

	/**
	 * Process login request
	 */
	login(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Check if all form fields are validat by (simple front end validation before doing anything
		 */
		if (!form.validateAll()) {
			return;
		}

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();
		data.append('action', 'accounts_login_action');
		data.append('email', this.loginEmailTarget.value);
		data.append('password', this.loginPasswordTarget.value);
		// data.append('redirect', document.location.href);
		data.append('post_id', this.postIdValue);

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * Set form error state, include error message
					 * to be displayed from response
					 */
					form.setErrorState(response.data.error);

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If user logged in succesfully but needs
					 * to give consent for specified post_id
					 */
					if (response.data.needs_consent) {
						/**
						 * Consent message/form is always at step 2
						 */
						this.activateStep(2);
					} else {
						/**
						 * If all is good, just reload the page.
						 */
						this.reload();
					}
				}
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	consent(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Check if all form fields are validat by (simple front end validation before doing anything
		 */
		if (!form.validateAll()) {
			return;
		}

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();

		data.append('action', 'save_consent_v2_action');

		/**
		 * Only for collection watchlist.
		 */
		if (this.collectionIdValue) {
			data.append('collection_id', this.collectionIdValue);
		}

		if (this.postIdValue) {
			data.append('post_id', this.postIdValue);
		}

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * @todo Display error message
					 */
					console.error(response.data.error);

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If all is good, just reload the page
					 */
					this.reload();
				}
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	submitGuestAccess(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Check if all form fields are validat by (simple front end validation before doing anything
		 */
		if (!form.validateAll()) {
			return;
		}

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();

		data.append('action', 'save_consent_v2_action');

		/**
		 * Only for collection watchlist.
		 */
		if (this.collectionIdValue) {
			data.append('collection_id', this.collectionIdValue);
		}

		if (this.postIdValue) {
			data.append('post_id', this.postIdValue);
		}

		this.guestAccessFieldTargets.forEach((field) => {
			data.append(field.name, field.value);
		});

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * @todo Display error message
					 */
					console.error(response.data.error);

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If all is good, just reload the page
					 */
					this.reload();
				}
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	consentSession(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Check if all form fields are validat by (simple front end validation before doing anything
		 */
		if (!form.validateAll()) {
			return;
		}

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();

		data.append('action', 'store_session_consent_v2_action');

		this.guestAccessFieldTargets.forEach((field) => {
			data.append(field.name, field.value);
		});

		/**
		 * Only for collection watchlist.
		 */
		if (this.collectionIdValue) {
			data.append('collection_id', this.collectionIdValue);
		}

		if (this.postIdValue) {
			data.append('post_id', this.postIdValue);
		}

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * @todo Display error message
					 */
					console.error(response.data.error);

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If all is good, just reload the page
					 */
					this.reload();
				}
				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	consentUpcoming(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Check if all form fields are validat by (simple front end validation before doing anything
		 */
		if (!form.validateAll()) {
			return;
		}

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();

		data.append('action', 'save_upcoming_consent_v2_action');

		this.guestAccessFieldTargets.forEach((field) => {
			data.append(field.name, field.value);
		});

		/**
		 * Only for collection watchlist.
		 */
		if (this.collectionIdValue) {
			data.append('collection_id', this.collectionIdValue);
		}

		if (this.postIdValue) {
			data.append('post_id', this.postIdValue);
		}

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				console.log(response);

				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * @todo Display error message
					 */
					console.error(response.data.error);

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If all is good, just reload the page
					 */

					window.location.href = response.data.redirect;

					// this.reload();
				}
				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	consentMagicLink(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Check if all form fields are validat by (simple front end validation before doing anything
		 */
		if (!form.validateAll()) {
			return;
		}

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();

		console.log(data);

		data.append('action', 'store_magic_link_consent_v2_action');

		/**
		 * Only for collection watchlist.
		 */
		if (this.collectionIdValue) {
			data.append('collection_id', this.collectionIdValue);
		}

		if (this.postIdValue) {
			data.append('post_id', this.postIdValue);
		}

		const url = window.location.href;

		const urlParams = new URLSearchParams(window.location.search);
		const accessToken = urlParams.get('access_token');

		if (accessToken) {
			data.append('access_token', accessToken);
		}

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * @todo Display error message
					 */
					const errorElement = document.getElementById('consent-magic-link-error');

					if (errorElement) {
						errorElement.innerHTML = response.data;
						errorElement.classList.remove('hidden');
					}

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If all is good, just reload the page
					 */
					this.reload();
				}
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	unlockContent(e) {
		e.preventDefault();

		/**
		 * Trick to grab the Stimulus controller for the form that was
		 * submitted, see https://discuss.hotwired.dev/t/getting-parent-controller-from-within-child/1053/8?u=microkid
		 */
		const form = e.currentTarget.controller;

		/**
		 * Initiate process and set form as loading
		 */
		form.setLoadingState();

		/**
		 * Let the backend know we are doing AJAX
		 */
		const headers = {
			Accept: 'application/json',
		};

		/**
		 * Collect POST parameters
		 */
		let data = new URLSearchParams();

		data.append('action', 'unlock_content_action');
		data.append('post_id', this.postIdValue);

		/**
		 * Send the request
		 */
		fetch(this.urlValue, {
			method: 'POST',
			headers: headers,
			body: data,
		})
			.then((response) => {
				/**
				 * Parse the JSON response
				 */
				return response.json();
			})
			.then((response) => {
				/**
				 * On error
				 */
				if (response.success === false) {
					/**
					 * @todo Display error message
					 */
					console.error(response.data.error);

					/**
					 * If something happened, remove the loading
					 * state so the user can try again
					 */
					form.removeLoadingState();
				} else {
					/**
					 * If all is good just reload the page
					 */
					this.reload();
				}
			})
			.catch((error) => {
				/**
				 * Log any errors to the console
				 */
				console.error(error);

				/**
				 * If something happened, remove the loading
				 * state so the user can try again
				 */
				form.removeLoadingState();
			});
	}

	reload() {
		/**
		 * Make sure we don't end up half way through
		 * the page after reloading
		 */
		window.onbeforeunload = function () {
			window.scrollTo(0, 0);
		};

		window.location.reload();
	}
}
